/**
 * Workshop Booking - price resolution engine.
 *
 * resolvePriceCents + its matching helpers, extracted verbatim from
 * workshop-booking.js. Self-contained factory: it carries its own copies of the
 * tiny match/normalize helpers so the resolver body is byte-for-byte the same as
 * before. Rule sets and the product id are injected via create(); everything
 * else (window debug flags, __WB_DIAG__) is read at call time exactly as before.
 *
 * Loaded (defer) before workshop-booking.js. Exposes window.WorkshopBookingPricing.
 *
 * Usage (in workshop-booking.js):
 *   var p = WorkshopBookingPricing.create({ locationRules, venueOverrides,
 *     eventTimeOverrides, productId, debug });
 *   var cents = p.resolve(params, testOverrides);   // returns integer cents
 */
(function (global) {
  if (global.WorkshopBookingPricing) return;

  function wbDiag(label, payload) {
    if (typeof window === 'undefined' || !window.__WB_DIAG__) return;
    console.debug('[WB_DIAG]', label, payload);
  }

  function create(cfg) {
    cfg = cfg || {};
    var locationRules = cfg.locationRules || [];
    var venueOverrides = cfg.venueOverrides || [];
    var eventTimeOverrides = cfg.eventTimeOverrides || [];
    var WB_PRICE_DEBUG = !!cfg.debug;
    var productId = cfg.productId != null ? String(cfg.productId) : '';

    var normalize = function (v) { return (v || '').toString().trim(); };
    var normKey = function (v) { return normalize(v).toLowerCase(); };

    function isBlank(val) {
      return val == null || normalize(val) === '';
    }

    function matchDim(ruleVal, actualVal) {
      // wildcard matching: blank = match any
      if (isBlank(ruleVal)) return true;
      return normKey(ruleVal) === normKey(actualVal);
    }

    /** Canonical event id for matching: Liquid outputs metaobject system.id (numeric); card may have ev.id (numeric) or ev.event_metaobject_gid (GID). */
    function normalizeEventIdForMatch(val) {
      if (val == null) return '';
      var s = String(val).trim();
      if (s === '') return '';
      var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
      if (metaGid) return metaGid[1];
      if (/^\d+$/.test(s)) return s;
      return s;
    }

    function matchEventId(selectedEventId, ruleWorkshopEventId) {
      if (ruleWorkshopEventId == null || String(ruleWorkshopEventId).trim() === '') return false;
      var a = normalizeEventIdForMatch(selectedEventId);
      var b = normalizeEventIdForMatch(ruleWorkshopEventId);
      return a !== '' && b !== '' && a === b;
    }

    function matchVariant(ruleVariantId, selectedVariantId) {
      // wildcard matching: blank = match any
      if (ruleVariantId == null || String(ruleVariantId) === '') return true;
      return String(ruleVariantId) === String(selectedVariantId);
    }

    function matchVariantExact(ruleVariantId, selectedVariantId) {
      return String(ruleVariantId) === String(selectedVariantId);
    }

    /** Normalize override amount to integer cents. Values < 500 treated as dollars (e.g. 10 -> 1000); >= 10000 and divisible by 100 may be double-converted (e.g. 100000 -> 1000); else assume already cents. */
    function toCentsInt(n) {
      if (n == null) return null;
      var x = Number(n);
      if (!Number.isFinite(x)) return null;
      if (Math.abs(x) < 500) return Math.round(x * 100);
      if (Math.abs(x) >= 10000 && Math.abs(x) % 100 === 0) return Math.round(x / 100);
      return Math.round(x);
    }

    function matchLocationScope(ruleScope, ruleVariantId, selectedVariantId) {
      // EXACT per requirements:
      // - all_variants => ignore rule.variant entirely
      // - single_variant => rule.variant MUST be present AND match selected variant
      const s = normKey(ruleScope);
      if (s === 'all_variants') return true;
      if (s === 'single_variant') {
        if (ruleVariantId == null || String(ruleVariantId) === '') return false;
        return matchVariantExact(ruleVariantId, selectedVariantId);
      }
      return false;
    }

    function specificityCount(fields) {
      // counts how many rule dimensions are explicitly set (more = more specific)
      return fields.reduce((acc, v) => (isBlank(v) ? acc : acc + 1), 0);
    }

    function pickBest(candidates, scoreFn) {
      let best = null;
      let bestScore = -1;
      let bestId = null;
      for (const c of candidates) {
        const score = scoreFn(c);
        const id = c && c.id != null ? String(c.id) : '';
        if (score > bestScore) {
          best = c;
          bestScore = score;
          bestId = id;
          continue;
        }
        if (score === bestScore && best != null) {
          // deterministic tie-break: smallest id wins
          if (id && bestId && id < bestId) {
            best = c;
            bestId = id;
          }
        }
      }
      return best;
    }

    function resolvePriceCents({ basePriceCents, eventId, state, city, cityKey, venueName, venueHandle, variantId, workshopId: providedWorkshopId }, testOverrides) {
      const locs = (testOverrides && testOverrides.locationRules) || locationRules;
      const venues = (testOverrides && testOverrides.venueOverrides) || venueOverrides;
      const eventTimes = (testOverrides && testOverrides.eventTimeOverrides) || eventTimeOverrides;

      const base = basePriceCents;
      const eId = String(eventId || '');
      const vId = String(variantId || '');
      const venueHandleNorm = normKey(venueHandle || '');
      const selectedCityKey = (cityKey || '').toString().trim();
      const selectedCityKeyNorm = normKey(selectedCityKey);
      const workshopId = providedWorkshopId ? String(providedWorkshopId) : productId;
      if (WB_PRICE_DEBUG && eId && typeof window !== 'undefined' && !window.__wbSelectedEventKeyLogged) {
        window.__wbSelectedEventKeyLogged = true;
        try {
          console.log('[WB] selected event key', { raw: eId, normalized: normalizeEventIdForMatch(eId) });
        } catch (_) {}
      }
      const ctx = { variantId: vId, basePriceCents: base, state, city, venueName, workshopEventId: eId };
      wbDiag('resolvePriceCents:context', ctx);
      if (base == null || (typeof base !== 'number') || Number.isNaN(base)) {
        wbDiag('resolvePriceCents:baseMissingOrInvalid', { base, basePriceCents });
      }

      // 1) Event time override - workshop_event_id required (match by canonical id: numeric or GID); optional geo/venue/variant with matchDim (blank = match any for that event)
      // JSON keys: workshop_event_id, variant_id, adjustment_type, pricing_status, amount_cents, percent (amount_cents from t.amount; fallback price_cents for compat)
      const etoCandidates = eventTimes.filter((o) => {
        if (!o) return false;
        if (!matchEventId(eId, o.workshop_event_id)) return false;
        if (!matchDim(o.state, state)) return false;
        if (!matchDim(o.city, city)) return false;
        if (!matchDim(o.venue_name, venueName)) return false;
        if (!matchVariant(o.variant_id, vId)) return false;
        return true;
      });
      if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbETOCandidatesLogged) {
        window.__wbETOCandidatesLogged = true;
        try {
          var first3 = etoCandidates.slice(0, 3).map(function (o) {
            return {
              workshop_event_id_raw: o && o.workshop_event_id,
              workshop_event_id_normalized: o ? normalizeEventIdForMatch(o.workshop_event_id) : null,
              variant_id: o && o.variant_id,
              adjustment_type: o && o.adjustment_type,
              amount_cents: o && o.amount_cents,
              percent: o && o.percent
            };
          });
          console.log('[WB] etoCandidates stats', {
            eId_normalized: normalizeEventIdForMatch(eId),
            etoCandidatesCount: etoCandidates.length,
            eventTimesTotal: eventTimes.length,
            first3: first3
          });
        } catch (_) {}
      }
      const eto = pickBest(etoCandidates, (o) =>
        specificityCount([o.workshop_event_id, o.state, o.city, o.venue_name, o.variant_id])
      );
      var rawAmount = eto && (eto.amount_cents != null ? eto.amount_cents : eto.price_cents != null ? eto.price_cents : null);
      var amountCents = rawAmount != null ? toCentsInt(rawAmount) : null;
      if (eto && amountCents != null) {
        const adjType = normKey(eto.adjustment_type);
        var afterCents;
        if (adjType === 'percent' && typeof eto.percent === 'number' && Number.isFinite(eto.percent)) {
          afterCents = Math.round(base * (1 + eto.percent / 100));
        } else if (adjType === 'delta') {
          afterCents = base + amountCents;
        } else if (adjType === 'fixed_price') {
          afterCents = amountCents;
        } else {
          var legacyDelta = amountCents < 0;
          afterCents = legacyDelta ? base + amountCents : amountCents;
        }
        var result = Math.max(0, Math.round(afterCents));
        if (!Number.isFinite(result) || result <= 0) {
          wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: null, reason: 'invalidResult' });
        } else {
        if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbPriceUnitLogged) {
          window.__wbPriceUnitLogged = true;
          try {
            console.log('[WB] event_time override units', {
              base: base,
              raw_eto_amount_cents: eto.amount_cents,
              raw_eto_price_cents: eto.price_cents,
              adjustment_type: eto.adjustment_type,
              amountCents_normalized: amountCents,
              afterCents: afterCents,
              result: result
            });
          } catch (_) {}
        }
        if (WB_PRICE_DEBUG && !window.__wbPriceDebugLogged) {
          try {
            window.__wbPriceDebugLogged = true;
            var firstOverride = eventTimes[0];
            console.log('[WB] resolvePriceCents event_time', {
              selectedEventKeyRaw: eId,
              selectedEventKeyNormalized: normalizeEventIdForMatch(eId),
              firstOverrideWorkshopEventIdRaw: firstOverride ? firstOverride.workshop_event_id : null,
              firstOverrideWorkshopEventIdNormalized: firstOverride ? normalizeEventIdForMatch(firstOverride.workshop_event_id) : null,
              adjustment_type: eto.adjustment_type,
              amount_cents: amountCents,
              percent: eto.percent,
              baseCents: base,
              resolvedCents: result
            });
          } catch (_) {}
        }
        wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: eto, amount_cents: amountCents, centsBefore: base, centsAfter: result });
        return result;
        }
      }
      wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: null });

      // 2) Venue override - match by venue_handle (prefer) or venue_name; city/state when set; variant per scope. amount_cents from t.amount (integer cents).
      const venueNameNorm = normKey(venueName);
      function normVenueName(s) { return normKey((s || '').toString().replace(/^venue\s*#?\s*/i, '').trim()); }
      const voCandidates = venues.filter((o) => {
        if (!o) return false;
        var venueMatch = (venueHandleNorm && normKey(o.venue_handle) === venueHandleNorm) ||
          (venueNameNorm && normKey(o.venue_name) === venueNameNorm) ||
          (venueNameNorm && normVenueName(o.venue_name) === normVenueName(venueName));
        if (!venueMatch) return false;
        if (!isBlank(o.city) && normKey(o.city) !== normKey(city)) return false;
        if (!isBlank(o.state) && normKey(o.state) !== normKey(state)) return false;
        if (!isBlank(o.scope)) {
          const s = normKey(o.scope);
          if (s === 'single_variant') {
            if (o.variant_id == null || String(o.variant_id) === '') return false;
            if (!matchVariantExact(o.variant_id, vId)) return false;
          } else if (s !== 'all_variants') return false;
        }
        if (o.variant_id != null && String(o.variant_id) !== '' && !matchVariantExact(o.variant_id, vId)) return false;
        return true;
      });
      const vo = pickBest(voCandidates, (o) =>
        specificityCount([o.venue_handle, o.venue_name, o.state, o.city, o.scope, o.variant_id])
      );
      var voRaw = vo && (vo.amount_cents != null ? vo.amount_cents : vo.price_cents != null ? vo.price_cents : null);
      var voCents = voRaw != null ? toCentsInt(voRaw) : null;
      if (vo && voCents != null) {
        const voAdjType = normKey(vo.adjustment_type);
        var voAfterCents;
        if (voAdjType === 'percent' && typeof vo.percent === 'number' && Number.isFinite(vo.percent)) {
          voAfterCents = Math.round(base * (1 + vo.percent / 100));
        } else if (voAdjType === 'delta') {
          voAfterCents = base + voCents;
        } else if (voAdjType === 'fixed_price' || voAdjType === 'fixed') {
          voAfterCents = voCents;
        } else {
          voAfterCents = voCents < 0 ? base + voCents : voCents;
        }
        const voResult = Math.max(0, Math.round(voAfterCents));
        if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbVenueOverrideLogged) {
          window.__wbVenueOverrideLogged = true;
          try {
            console.log('[WB] venue override', { venueName, venueHandle, voRaw, voCents, adjustment_type: vo.adjustment_type, base, voResult });
          } catch (_) {}
        }
        if (!Number.isFinite(voResult) || voResult <= 0) {
          wbDiag('resolvePriceCents:venueMatch', { venueMatch: null, reason: 'invalidResult' });
        } else {
          wbDiag('resolvePriceCents:venueMatch', { venueMatch: vo, amount_cents: voCents, centsBefore: base, centsAfter: voResult });
          return voResult;
        }
      }
      wbDiag('resolvePriceCents:venueMatch', { venueMatch: null });

      // 3) Location rule - workshop + scope/variant match; location: city_key exact, or location_query contained in city_key, or both blank = global
      // Ignore rule.state. pricing_status must be active.
      function locationRuleMatches(r) {
        if (!r) return false;
        if (normKey(r.pricing_status) !== 'active') return false;
        if (r.workshop_id != null && String(r.workshop_id) !== '' && String(r.workshop_id) !== workshopId) return false;
        if (!matchLocationScope(r.scope, r.variant_id, vId)) return false;
        var rCityKey = (r.city_key || '').toString().trim();
        var rLocQuery = (r.location_query || '').toString().trim();
        if (rCityKey !== '') {
          if (normKey(rCityKey) !== selectedCityKeyNorm) return false;
        } else if (rLocQuery !== '') {
          var qNorm = normKey(rLocQuery);
          if (!qNorm || selectedCityKeyNorm.indexOf(qNorm) === -1) return false;
        }
        var amt = r.amount_cents != null ? r.amount_cents : r.price_cents;
        if (amt == null && (r.percent == null || !Number.isFinite(r.percent))) return false;
        return true;
      }
      const locCandidates = locs.filter(locationRuleMatches);
      if (locCandidates.length > 0) {
        const getTier = (r) => {
          if (normKey(r.scope) === 'single_variant' && r.variant_id != null && String(r.variant_id) !== '' && matchVariantExact(r.variant_id, vId)) return 1;
          if (!isBlank(r.city_key)) return 2;
          if (!isBlank(r.location_query)) return 3;
          return 4;
        };
        const bestTier = Math.min(...locCandidates.map(getTier));
        const tierCandidates = locCandidates.filter((r) => getTier(r) === bestTier);
        const numId = (r) => { const n = parseInt(r && r.id, 10); return Number.isFinite(n) ? n : Infinity; };
        let locWinner = null;
        let bestWorkshopScore = -1;
        let bestNumId = Infinity;
        for (const r of tierCandidates) {
          const ws = (r.workshop_id != null && String(r.workshop_id) !== '') ? 1 : 0;
          const nid = numId(r);
          if (ws > bestWorkshopScore || (ws === bestWorkshopScore && nid < bestNumId)) {
            locWinner = r;
            bestWorkshopScore = ws;
            bestNumId = nid;
          }
        }
        if (locWinner) {
          if (WB_PRICE_DEBUG && locWinner && locWinner.amount_cents != null) {
            const rawAmount = locWinner.amount_cents;

            // Suspiciously small cents (likely dollars accidentally passed as cents)
            if (Math.abs(Number(rawAmount)) < 50) {
              console.warn('[WB] Suspicious small amount_cents in location rule', {
                id: locWinner.id,
                amount_cents: rawAmount
              });
            }

            // Decimal detected in cents (should always be integer)
            if (String(rawAmount).includes('.')) {
              console.warn('[WB] Decimal detected in amount_cents (should be integer cents)', {
                id: locWinner.id,
                amount_cents: rawAmount
              });
            }
          }
          const adj = normKey(locWinner.adjustment_type || '');
          var result = null;
          if (adj === 'percent') {
            var pct = Number(locWinner.percent);
            if (Number.isFinite(pct)) result = Math.round(base * (1 + pct / 100));
          } else {
            var amount = locWinner.amount_cents != null ? locWinner.amount_cents : locWinner.price_cents;
            amount = amount != null ? toCentsInt(amount) : null;
            if (amount != null) {
              if (adj === 'fixed' || adj === 'fixed_price') result = amount;
              else if (adj === 'delta') result = base + amount;
              else result = null;
            }
          }
          if (result != null && Number.isFinite(result) && result > 0) {
            if (WB_PRICE_DEBUG && typeof window !== 'undefined') {
              try { console.log('[WB] location applied', { id: locWinner.id, adjustment_type: locWinner.adjustment_type, amount_cents: locWinner.amount_cents, percent: locWinner.percent, base: base, resolvedCents: result }); } catch (_) {}
            }
            wbDiag('resolvePriceCents:locationRuleMatch', { locationRuleMatch: locWinner, amount_cents: locWinner.amount_cents, centsBefore: base, centsAfter: result });
            return result;
          }
        }
      }
      wbDiag('resolvePriceCents:locationRuleMatch', { locationRuleMatch: null });

      wbDiag('resolvePriceCents:fallbackBase', { cents: base });
      return base;
    }

    return { resolve: resolvePriceCents };
  }

  global.WorkshopBookingPricing = { create: create };
})(typeof window !== 'undefined' ? window : this);
